import { Form } from 'react-final-form';
import { ConfirmationDialog } from '@src/design-system/dialogs/ConfirmationDialog';
import { ApolloError } from '@apollo/client';
import { ServerValidationErrors } from '@src/design-system/forms/ServerValidationErrors';
import { Validator } from '@src/design-system/forms/validators';
import { AutocompleteField } from '@src/design-system/forms/autocomplete/AutocompleteField';
import { TextField } from 'mui-rff';
import { Stack } from '@mui/system';
import { Button, Typography } from '@mui/material';
import { WarningMessage } from '@src/design-system/warning-message/WarningMessage';
import { UserRole } from '@flashpack/graphql';
import { Protected } from '@src/authentication/Protected';

import { SourceItinerary } from './AdventureItinerariesTab';
import { FC } from 'react';
import {
  AdventureOption,
  useOtherAdventureOptions,
} from '@src/adventure/common/useOtherAdventureOptions';

export type DuplicateItineraryForm = {
  newDescription: string;
  targetAdventureId?: string;
};

type DuplicateItineraryDialogProps = {
  sourceItinerary: SourceItinerary;
  currentAdventureId: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (values: DuplicateItineraryForm) => Promise<unknown>;
};

export const DuplicateItineraryDialog: FC<DuplicateItineraryDialogProps> = ({
  sourceItinerary,
  currentAdventureId,
  open,
  onClose,
  onSubmit,
}) => {
  const { options: adventureOptions } = useOtherAdventureOptions(currentAdventureId, {
    skip: !open,
  });

  return (
    <ConfirmationDialog
      title={`Duplicate ${sourceItinerary.description}?`}
      open={open}
      onCancel={onClose}
      hasCustomActions
    >
      <Form<DuplicateItineraryForm>
        onSubmit={onSubmit}
        render={({ handleSubmit, dirtySinceLastSubmit, submitError }) => (
          <>
            <form id="duplicateForm" onSubmit={(event) => void handleSubmit(event)}>
              <Stack gap={2}>
                <WarningMessage
                  iconType="WARNING"
                  containerSx={{ my: 0 }}
                  message={
                    <Typography variant="Body S">
                      <strong>IMPORTANT:</strong> If duplicating to over 50s product,
                      remember to update rooming allocations as necessary
                    </Typography>
                  }
                />
                <TextField
                  label="New Version Name"
                  name="newDescription"
                  fieldProps={{ validate: Validator.required }}
                />
                <Protected roles={[UserRole.Flashpack]}>
                  <Stack gap={0.5}>
                    <AutocompleteField<AdventureOption, false, false, false>
                      name="targetAdventureId"
                      label="Copy to another adventure (optional)"
                      options={adventureOptions}
                      getOptionValue={(option) => option.value}
                      data-testid="duplicate-itinerary-target-adventure"
                    />
                    <Typography variant="captionPara" color="principal.grey70">
                      Leave empty to keep the copy on this adventure. Departures are not
                      copied.
                    </Typography>
                  </Stack>
                </Protected>
              </Stack>
            </form>
            {!dirtySinceLastSubmit && (
              <ServerValidationErrors error={submitError as ApolloError} />
            )}
            <Stack direction="row" gap={3} justifyContent="flex-end">
              <Button variant="outlined" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" form="duplicateForm" variant="contained">
                Duplicate
              </Button>
            </Stack>
          </>
        )}
      />
    </ConfirmationDialog>
  );
};
